<script lang="ts" generics="BtnHref extends string|undefined">
	import captureClick from '$lib/actions/captureClick';
	import { type Snippet } from 'svelte';
	import type { HTMLAttributes } from 'svelte/elements';

	interface Props extends HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
		href?: BtnHref;
		target?: BtnHref extends string ? HTMLAnchorElement['target'] : never;
		type?: BtnHref extends string ? never : HTMLButtonElement['type'];
		disabled?: BtnHref extends string ? never : HTMLButtonElement['disabled'];
		children: Snippet;
		buttonRef?: HTMLButtonElement | HTMLAnchorElement;
		fitContent?: boolean;
		[key: string]: any;
	}

	let {
		href,
		target,
		type,
		disabled,
		children,
		class: className,
		tabindex = 0,
		buttonRef = $bindable(),
		fitContent = true,
		...other
	}: Props = $props();

	let element = $derived(href ? 'a' : 'button') as 'a' | 'button';
	let btnType = $derived(element === 'button' ? (type ?? 'button') : undefined);

	let isTertiary = $derived.by(() => {
		if (!className) return false;

		return Array.isArray(className)
			? className.includes('btn-tertiary')
			: Object.keys(className).includes('btn-tertiary');
	});
</script>

<svelte:element
	this={element}
	bind:this={buttonRef}
	use:captureClick
	{...other}
	role={element === 'button' ? null : 'button'}
	{tabindex}
	type={btnType}
	{href}
	{target}
	{disabled}
	class={[
		className,
		'btn-mill group relative inline-flex select-none items-center justify-center overflow-hidden text-center',
		{ 'min-w-fit': fitContent, 'cursor-not-allowed': disabled }
	]}
	data-sveltekit-noscroll={(href && href.startsWith('?')) || null}
>
	<div class="pointer-events-none absolute inset-0 z-0 h-full w-full max-lg:hidden"></div>
	<div
		class={[
			'relative z-10 flex shrink-0 items-center justify-center',
			{
				'whitespace-nowrap': !isTertiary,
				'text-pretty': isTertiary
			}
		]}
	>
		{@render children()}
	</div>
</svelte:element>

<style lang="scss">
	@use './themes.scss';

	:root {
		--transition-duration: 360ms;

		// default theme: green
		--bg-color: theme('colors.green.3');
		--text-color: theme('colors.white');
		--hover-bg-color: theme('colors.green.2');
		--hover-text-color: theme('colors.white');

		// secondary styles
		--secondary-bg-color: transparent;

		// tertiary styles
		--hover-opacity: 0.08;
		--tertiary-bg-color: transparent;

		// icon styles
		--icon-size: theme('spacing.6');
	}

	.btn-mill {
		background-color: var(--bg-color);
		color: var(--text-color);

		line-height: 100%;

		// default: medium size
		padding: 18.5px theme('spacing.7');
		border-radius: 52px;
		font-size: 16px;
		letter-spacing: -0.16px;
		height: 48px;

		&.btn-tertiary {
			& > div.absolute {
				background-color: rgb(from var(--text-color) r g b / var(--hover-opacity));
			}
		}

		& > div.absolute {
			background-color: var(--hover-bg-color);
			border-radius: 52px;

			transform: translate3d(0, 100%, 0);
			transition-timing-function: cubic-bezier(0.25, 1, 0.5, 1);
			transition-duration: var(--transition-duration);
			transition-property: transform;
		}

		@media (min-width: 1024px) {
			&:hover:not(:active) {
				& > div.absolute {
					transform: translate3d(0, 0, 0);
				}

				& > div.relative {
					color: var(--hover-text-color);
				}
			}

			&:focus {
				&:not(.btn-secondary) {
					outline-width: 0;
				}

				& > div.absolute {
					transform: translate3d(0, 0, 0);
				}

				& > div.relative {
					color: var(--hover-text-color);
				}
			}
		}

		& > div.relative {
			column-gap: theme('spacing.1');

			transition-timing-function: cubic-bezier(0.25, 1, 0.5, 1);
			transition-duration: var(--transition-duration);
			transition-property: color;

			& > :global(svg) {
				width: var(--icon-size);
				height: var(--icon-size);
			}
		}

		&:has(> div.relative) {
			&:has(:global(svg:only-child)) {
				border-radius: theme('borderRadius.full');
				padding: theme('spacing.3');
			}

			&:has(:global(svg:first-child:not(:only-child))) {
				padding-left: theme('spacing.6');
			}

			&:has(:global(svg:last-child:not(:only-child))) {
				padding-right: theme('spacing.6');
			}
		}

		&.btn-xs {
			--icon-size: theme('spacing.5');

			padding: theme('spacing.3') theme('spacing.4');
			border-radius: 44px;
			font-size: 12px;
			letter-spacing: normal;
			height: 32px;

			& > div.absolute {
				border-radius: 44px;
			}

			&:has(> div.relative) {
				&:has(:global(svg:only-child)) {
					padding: theme('spacing[1.5]');
				}

				&:has(:global(svg:first-child:not(:only-child))) {
					padding-left: theme('spacing.3');
				}

				&:has(:global(svg:last-child:not(:only-child))) {
					padding-right: theme('spacing.3');
				}
			}
		}

		&.btn-small {
			--icon-size: theme('spacing.5');

			padding: 15px theme('spacing.5');
			border-radius: 44px;
			font-size: 14px;
			letter-spacing: normal;
			height: 40px;

			& > div.absolute {
				border-radius: 44px;
			}

			&:has(> div.relative) {
				&:has(:global(svg:only-child)) {
					padding: theme('spacing[2.5]');
				}

				&:has(:global(svg:first-child:not(:only-child))) {
					padding-left: theme('spacing.4');
				}

				&:has(:global(svg:last-child:not(:only-child))) {
					padding-right: theme('spacing.4');
				}
			}
		}

		&.btn-large {
			padding: 21.5px theme('spacing.9');
			border-radius: 58px;
			font-size: 18px;
			letter-spacing: -0.18px;
			height: 56px;

			& > div.absolute {
				border-radius: 58px;
			}

			& > div.relative {
				column-gap: theme('spacing.2');
			}

			&:has(> div.relative) {
				&:has(:global(svg:only-child)) {
					padding: theme('spacing.4');
				}

				&:has(:global(svg:first-child:not(:only-child))) {
					padding-left: theme('spacing.8');
				}

				&:has(:global(svg:last-child:not(:only-child))) {
					padding-right: theme('spacing.8');
				}
			}
		}

		&.btn-secondary {
			background-color: var(--secondary-bg-color);
			color: var(--bg-color);
			outline-style: solid;
			outline-width: 1px;
			outline-color: var(--bg-color);
			outline-offset: -1px;
		}

		&.btn-tertiary {
			background-color: var(--tertiary-bg-color);
		}

		&.btn-blur {
			backdrop-filter: blur(12px);

			&:hover {
				background-color: var(--hover-bg-color);
				transition-property: background-color;
				transition-duration: var(--transition-duration);
			}

			& > .absolute {
				display: none;
			}
		}
	}
</style>
